import { Directive, ElementRef, HostListener, NgZone, OnInit } from '@angular/core';
import { BehaviorSubject, combineLatest, from, fromEvent, of } from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { User } from '../models/user';
import { ProfilesService } from '../services/profiles.service';
import { NotificationToasterService } from '../services/notification-toaster.service';
import { TOAST_CLASSES } from '../common/utils/notification-constants';
import { modifiedDebounceTime } from '../utilities/ZoneUtils';
import { UserSearchSelectionService } from '../services/user-search-selection.service';

@UntilDestroy()
@Directive({
  selector: '[appUserSearchInput]',
  standalone: true,
})
export class UserSearchInputDirective implements OnInit {
  // Search configuration constants
  private readonly DEBOUNCE_TIME = 300;
  private readonly MAX_RESULTS = 100;
  /** Do not call the backend until the trimmed query has at least this many characters. */
  private readonly MIN_SEARCH_QUERY_LENGTH = 3;

  private readonly filteredUsersSubject = new BehaviorSubject<User[]>([]);
  /** Latest backend search results (also emits `[]` when query is cleared). */
  readonly filteredUsers$ = this.filteredUsersSubject.asObservable();

  /** Synchronous snapshot of {@link filteredUsers$} (e.g. keyboard navigation). */
  get filteredUsers(): User[] {
    return this.filteredUsersSubject.value;
  }

  /** Observable loading state — true while a backend search is in flight. */
  readonly isLoading$ = new BehaviorSubject<boolean>(false);

  constructor(
    private el: ElementRef,
    private profilesService: ProfilesService,
    private notificationToasterService: NotificationToasterService,
    private translateService: TranslateService,
    private zone: NgZone,
    private userSearchSelectionService: UserSearchSelectionService,
  ) {}

  ngOnInit(): void {
    const el = this.el.nativeElement as HTMLInputElement;

    // Setup search pipeline
    // Debounce runs outside zone for performance, but emit is wrapped in zone.run()
    // to ensure change detection runs when results are ready
    const queryChange$ = fromEvent<InputEvent>(el, 'input').pipe(
      map((event) => (event.target as HTMLInputElement).value || ''),
      modifiedDebounceTime(this.DEBOUNCE_TIME, true),
      distinctUntilChanged(),
    );

    // focus$ re-triggers the search when the user refocuses the input,
    // ensuring results reappear after the user clicks away and back.
    const focus$ = fromEvent(el, 'focus');

    combineLatest([queryChange$, focus$])
      .pipe(
        switchMap(([query]) => {
          const trimmedQuery = query.trim();
          if (trimmedQuery.length < this.MIN_SEARCH_QUERY_LENGTH) {
            return of<User[]>([]);
          }
          this.zone.run(() => this.isLoading$.next(true));
          return from(this.searchUsers(trimmedQuery));
        }),
        untilDestroyed(this),
      )
      .subscribe((users) => {
        this.zone.run(() => {
          this.isLoading$.next(false);
          this.filteredUsersSubject.next(users);
        });
      });
  }

  // Clear the search results and hide the loading spinner when the input is blurred
  @HostListener('blur')
  onBlur(): void {
    this.isLoading$.next(false);
    this.filteredUsersSubject.next([]);
  }

  /**
   * Clears the input and dispatches an 'input' event so the query stream sees ''
   * and the pipeline emits empty results. Call from the host when clearing programmatically.
   */
  clear(): void {
    const el = this.el.nativeElement as HTMLInputElement;
    el.value = '';
    el.dispatchEvent(new Event('input'));
  }

  private async searchUsers(query: string): Promise<User[]> {
    const excludedIds = this.userSearchSelectionService.selectedUserIds;
    const filters: any = {
      search: query,
      ...(excludedIds?.length > 0 ? { excludedIds } : {}),
    };

    try {
      const res = await this.profilesService.searchAccessibleUsers(1, this.MAX_RESULTS, filters);
      return res?.users ?? [];
    } catch (error) {
      this.notificationToasterService.showDefaultErrorNotificationV2(
        this.translateService.instant('Failed to search users'),
        this.translateService.instant('Something went wrong, please try again.'),
        this.notificationToasterService.generateNotificationCode(),
        undefined,
        undefined,
        undefined,
        true,
        undefined,
        TOAST_CLASSES.HIGH_Z_INDEX,
      );
      return [];
    }
  }
}
